/**
 * Identity + admin lookup for the current user. Backed by ``GET /api/me``,
 * which is the source of truth for ``is_admin`` (computed server-side from
 * ``ADMIN_EMAILS`` so the client can't claim admin without the backend
 * agreeing).
 *
 * Queried once via React Query and cached for the session — the answer
 * doesn't change while the JWT is valid, so we set a long ``staleTime``
 * to avoid refetching on every nav.
 */
import { useQuery } from "@tanstack/react-query";
import { useApi, type ApiError } from "@/lib/api";

export interface MeResponse {
  oid: string;
  preferred_username: string | null;
  name: string | null;
  roles: string[];
  is_admin: boolean;
  // Leadership: the Launches page is the only surface this user can reach.
  launcher_only: boolean;
  // Whether the Surveys page is offered at all (``SURVEYS_ON_THE_WEB``).
  surveys_on_the_web: boolean;
}

export function useMe() {
  const apiCall = useApi();
  return useQuery<MeResponse, ApiError>({
    queryKey: ["me"],
    queryFn: ({ signal }) => apiCall<MeResponse>("/api/me", { signal }),
    // The admin bit is bound to the JWT lifetime; refetching every minute
    // would be wasteful. Use ``Infinity`` so React Query treats it as
    // permanently fresh until the page reloads (and the auth gate
    // re-validates from scratch).
    staleTime: Infinity,
  });
}

/**
 * Convenience: ``true`` when the backend has confirmed the current user
 * is an admin. Returns ``false`` while the query is loading so the nav
 * starts in a "non-admin" state and progressively enhances when the
 * answer arrives — much better than flashing admin-only links to a
 * non-admin user.
 *
 * The ``use`` prefix is required so React's ``rules-of-hooks`` lint
 * recognizes this as a hook (it calls ``useMe`` internally). Call-site
 * reads cleanly as ``const isAdmin = useIsAdmin()``.
 */
export function useIsAdmin(): boolean {
  const { data } = useMe();
  return data?.is_admin === true;
}

/**
 * ``true`` when the backend says this user may see the Launches page only
 * (the Leadership Team's ``Launcher.Only`` app role). ``false`` while loading,
 * so nothing is hidden from a full member waiting on the answer; the route
 * gate holds the page until it arrives.
 */
export function useIsLauncherOnly(): boolean {
  const { data } = useMe();
  return data?.launcher_only === true;
}

/**
 * ``true`` when the deployment offers the Surveys page. ``false`` while
 * loading, like the admin bit, so a switched-off page never flashes into the
 * nav; ``SurveysGate`` holds the route until the answer arrives.
 */
export function useSurveysOnTheWeb(): boolean {
  const { data } = useMe();
  return data?.surveys_on_the_web === true;
}
